"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { Button } from "@xefi/x-react/button";
import { Drawer } from "@xefi/x-react/drawer";
import { IconArrowRight, IconHistory } from "@xefi/x-react/icons";
import { useTranslation } from "react-i18next";
import type { PaymentActivity } from "../../../../domain/models/PaymentActivity";
import { formatText, isDone } from "./paymentsFormat";
import { mapPaymentActivities, type HistoryChange } from "./paymentActivities";
type Props = {
  isOpen: boolean;
  onClose: () => void;
  activities: PaymentActivity[] | undefined;
  trackedFields: readonly string[];
  booleanFields?: readonly string[];
};
const isTruthyToDo = (value: unknown): boolean => value === true || value === 1 || value === "1";
const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};
export default function HistoryDrawer(t0) {
  const $ = _c(35);
  const {
    isOpen,
    onClose,
    activities,
    trackedFields,
    booleanFields: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? ["to_do"] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const booleanFields = t2;
  const {
    t
  } = useTranslation();
  let t3;
  if ($[2] !== activities || $[3] !== t || $[4] !== trackedFields) {
    t3 = mapPaymentActivities(activities, {
      fallbackUser: t("paymentsReceived.history.system"),
      trackedFields
    });
    $[2] = activities;
    $[3] = t;
    $[4] = trackedFields;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const lines = t3;
  let t4;
  if ($[6] !== booleanFields || $[7] !== t) {
    t4 = (field, value) => {
      if (booleanFields.includes(field)) {
        return isDone(isTruthyToDo(value)) ? t("paymentsReceived.yes") : t("paymentsReceived.no");
      }
      return formatText(value);
    };
    $[6] = booleanFields;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const formatValue = t4;
  let t5;
  if ($[9] !== formatValue || $[10] !== t) {
    t5 = change => <div key={change.field} className="flex flex-wrap items-center gap-2 text-xs"><span className="font-medium text-foreground/70">{t(`paymentsReceived.history.fields.${change.field}`)}</span><span className="min-w-0 whitespace-pre-wrap break-words rounded-md bg-danger/10 px-2 py-0.5 text-danger line-through decoration-danger/40">{formatValue(change.field, change.from)}</span><IconArrowRight size={14} className="shrink-0 text-foreground/40" /><span className="min-w-0 whitespace-pre-wrap break-words rounded-md bg-success/10 px-2 py-0.5 font-medium text-success">{formatValue(change.field, change.to)}</span></div>;
    $[9] = formatValue;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const renderChange = t5;
  let t6;
  if ($[12] !== onClose) {
    t6 = open => {
      if (!open) {
        onClose();
      }
    };
    $[12] = onClose;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <IconHistory size={20} className="text-primary" />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t("paymentsReceived.history.title");
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <span className="flex items-center gap-2">{t7}{t8}</span>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t) {
    t10 = t("paymentsReceived.history.close");
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== onClose || $[22] !== t10) {
    t11 = <Button variant="bordered" radius="sm" onClick={onClose}>{t10}</Button>;
    $[21] = onClose;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== lines || $[25] !== renderChange || $[26] !== t) {
    t12 = lines.length === 0 ? <div className="flex flex-col items-center justify-center gap-3 py-12 text-center"><div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary"><IconHistory size={26} /></div><p className="text-sm text-foreground/60">{t("paymentsReceived.history.empty")}</p></div> : <ul className="flex flex-col gap-6 py-1">{lines.map((line, index) => <li key={line.id} className="relative flex gap-4">{index !== lines.length - 1 && <span aria-hidden={true} className="absolute left-[17px] top-9 h-[calc(100%-0.75rem)] w-px bg-border dark:bg-[#3a3a3a]" />}<div className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{getInitials(line.user)}</div><div className="min-w-0 flex-1 rounded-xl border border-border bg-content1 p-3 dark:border-[#3a3a3a]"><div className="flex flex-wrap items-center justify-between gap-2"><span className="text-sm font-semibold text-foreground">{line.user}</span><div className="flex items-center gap-2">{index === 0 && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">{t("paymentsReceived.history.lastAction")}</span>}<span className="text-xs text-foreground/50">{line.date} · {line.time}</span></div></div><div className="mt-2 flex flex-col gap-1.5">{line.changes.map(renderChange)}</div></div></li>)}</ul>;
    $[24] = lines;
    $[25] = renderChange;
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== isOpen || $[29] !== onClose || $[30] !== t11 || $[31] !== t12 || $[32] !== t6 || $[33] !== t9) {
    t13 = <Drawer isOpen={isOpen} onClose={onClose} onOpenChange={t6} placement="right" size="lg" title={t9} footer={t11}>{t12}</Drawer>;
    $[28] = isOpen;
    $[29] = onClose;
    $[30] = t11;
    $[31] = t12;
    $[32] = t6;
    $[33] = t9;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
}